import React, { FC, useContext } from 'react'

import { useParams } from 'react-router'
import { motion } from 'framer-motion'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import { isNumber } from 'lodash'
import { useHistory } from 'react-router-dom'
import clsx from 'clsx'

import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../components/ui/tooltip'

import FirestoreService from '../../../../services/firestoreService'
import RegistrationDyDayService from '../../../../services/firebase-service/registration-by-day/RegistrationDyDayService'
import { HandleModalContext } from '../../../../layout/mainlayout/MainLayout'

import useToasterHelper from '../../../../helpers/ToasterHelper'
import { eligibleToSign } from '../../../../helpers/sign'
import { getRegistrations } from '../../../../helpers/getRegistrations'

import useTeamMembers from '../../../../hooks/useTeamMembers'
import useRegistrationFees from '../../../../hooks/useRegistrationFees'

import {
  selectPayTabGrandTotalAccordion,
  selectRegisterTabData,
  selectRegistration,
  selectRegistrationLoading,
  selectRegistrationsByDayInDb,
  selectRidersTeamMembersR,
  setRegistrationAc,
} from '../../../../store/registration/registrationSlice'
import { selectedEvent } from '../../../../store/events/eventsSlice'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectIsLoggedIn, selectProfileData } from '../../../../store/user/userSlice'

import { RegistrationByDayModel } from '../../../../models/registrations-by-day/registrationByDay.model'

import ITypes from '../hooks/useEventRegistrationTabs.types'
import { IDocumentList } from '../../../../fakeData/fakeDocumentList'
import {
  IRegistrationEventTab,
  IRegistrationEventTabsName,
} from '../../../../types/competitor_types'

import { CONST } from '../../../../const/const'
import { MESSAGES_CONST } from '../../../../const/messages-const'
import { MODAL_CONSTS } from '../../../../const/modal-const'
import { ROUTES_CONST } from '../../../../const/routes-const'

interface RegistrationButtonsProps {
  eventTab: IRegistrationEventTab
  setEventTab: (value: IRegistrationEventTab) => void
  tabs: { tabName: string; max_steps: number }[]
  isStepLoading: boolean
  setIsStepLoading: (value: boolean) => void
  createRegistration: () => Promise<any>
  filteredPaperworkDocuments: IDocumentList
  registrationHasErrors: {
    horses: boolean
    users: boolean
  }
  isSplitBill: boolean
}

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

export const RegistrationButtons: FC<RegistrationButtonsProps> = ({
  eventTab,
  setEventTab,
  tabs,
  isStepLoading,
  setIsStepLoading,
  createRegistration,
  filteredPaperworkDocuments,
  registrationHasErrors,
  isSplitBill,
}) => {
  const { eventId, userId } = useParams<ITypes['IParams']>()
  const registerTabData = useAppSelector(selectRegisterTabData)
  const grandTotalAccordionData = useAppSelector(selectPayTabGrandTotalAccordion)

  const history = useHistory()
  const { generateRidersTeamMembers } = useTeamMembers()
  const { generateMandatoryFees } = useRegistrationFees()

  const toastFunctions = useToasterHelper()
  const dispatch = useAppDispatch()

  const handleModalContext = useContext(HandleModalContext)
  const handleModal = handleModalContext?.handleModal

  const event = useAppSelector(selectedEvent)
  const registration = useAppSelector(selectRegistration)
  const isLoggedIn = useAppSelector(selectIsLoggedIn)
  const registrationLoading = useAppSelector(selectRegistrationLoading)
  const registrationsByDayInDb = useAppSelector(selectRegistrationsByDayInDb)
  const ridersTeamMembers = useAppSelector(selectRidersTeamMembersR)
  const profileData = useAppSelector(selectProfileData)

  // Handles the tab change on click of the next button
  const handleStep = async () => {
    setIsStepLoading(true)

    try {
      if (eventTab.tab === CONST.UI.REGISTER.TABS.HORSES) {
        if (!registration) return

        const registrations = getRegistrations(registerTabData)

        const hasEmptyFields = registrations.allRegistrations
          .filter((row) => !row.isDivision)
          .some(
            (registration) =>
              !(
                (registration.horsesSelected && registration.horsesSelected[0]) ||
                registration.horseId
              )
          )

        if (hasEmptyFields) {
          setIsStepLoading(false)
          return toastFunctions.error({
            message:
              'You must select a horse for each entry. If no horse is available, please choose "No Horse."',
            autoClose: 8000,
          })
        }

        const { registrationFeesUpdated } = await generateMandatoryFees({
          registrationsByDayInDb,
          publishedEvent: event,
        })
        const { ridersTeamMembersUpdated } = await generateRidersTeamMembers()

        const registrationUpdated = {
          ...registration,
          cartItems: {
            ...registration?.cartItems,
            fees: registrationFeesUpdated,
            ridersTeamMembers: ridersTeamMembersUpdated,
          },
        }
        await FirestoreService.updateItem(
          COLLECTIONS.EVENT_REGISTERED_USERS.NAME,
          registration?.id,
          registrationUpdated
        )
        dispatch(setRegistrationAc(registrationUpdated))
      } else if (eventTab.tab === CONST.UI.REGISTER.TABS.COMMENTS) {
        const errorMessage = 'Missing required comments for questions'
        if (event?.Event && event?.Event.questions) {
          const requiredFields = event.Event.questions.filter((q) => q.isRequired)
          if (requiredFields[0] && !registration?.answers?.[0]) {
            setIsStepLoading(false)
            return toastFunctions.error({ message: errorMessage })
          }

          const missingComments = requiredFields.filter((question) => {
            const hasValidComment = registration?.answers?.some(
              (answer) => answer.questionId === question.id && answer.answer.trim() !== ''
            )
            return !hasValidComment
          })

          if (missingComments[0]) {
            setIsStepLoading(false)
            return toastFunctions.error({ message: errorMessage })
          }
        }

        const registrationsByDay = await RegistrationDyDayService.getAllRidersByEventAndUser(
          eventId,
          userId
        )

        for (const registrationByDay of registrationsByDay) {
          const newData = new RegistrationByDayModel({ ...registrationByDay })

          await FirestoreService.updateItem(
            CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.REGISTRATION_BY_DAY.NAME,
            registrationByDay.id,
            newData.toFirestore()
          )
        }
        await FirestoreService.updateItem(
          COLLECTIONS.EVENT_REGISTERED_USERS.NAME,
          registration?.id,
          {
            answers: registration?.answers || [],
          }
        )
      } else if (eventTab.tab === CONST.UI.REGISTER.TABS.TEAM) {
        if (registrationHasErrors.horses || registrationHasErrors.users) {
          setIsStepLoading(false)
          return toastFunctions.error({ message: 'Profile has errors' })
        }

        await createRegistration()
      } else if (eventTab.tab === CONST.UI.REGISTER.TABS.PAPERWORK) {
        const isValid = ridersTeamMembers.every(
          (ridersTeamMember) => ridersTeamMember.teamMemberId && ridersTeamMember.teamMemberRole
        )
        if (!isValid) {
          setIsStepLoading(false)
          toastFunctions.error({
            message: 'Please fill in or remove the unfilled fields for team members.',
          })
          return
        }
      } else if (eventTab.tab === CONST.UI.REGISTER.TABS.SIGN) {
        const { notSignedOwner, isMinorRider, isMinorGuardian } = eligibleToSign({
          paperworkDocuments: filteredPaperworkDocuments,
          userId: registration?.userId ?? userId,
          ridersTeamMembers,
        })

        if (notSignedOwner) {
          setIsStepLoading(false)
          return toastFunctions.error({ message: MESSAGES_CONST.REGISTRATION_EVENT_NOT_SIGNED })
        } else if (isMinorRider) {
          // without "return" because minor rider can finish registration without "Guardian"
          toastFunctions.info({ message: MESSAGES_CONST.REGISTRATION_EVENT_RIDER_IS_MINOR })
        } else if (isMinorGuardian) {
          setIsStepLoading(false)
          return toastFunctions.error({
            message: MESSAGES_CONST.REGISTRATION_EVENT_GUARDIAN_IS_MINOR,
          })
        }
      } else if (eventTab.tab === CONST.UI.REGISTER.TABS.CONFIRMATION) {
        handleModal?.(true, MODAL_CONSTS.REGISTER_FOR_COMPETITION_PAY_BY_CARD, {
          isSplitBill: isSplitBill,
        })

        setIsStepLoading(false)
        return
      }

      scrollToTop()

      let currTabIndex
      tabs.forEach((currTab: any, index: number) => {
        if (eventTab.tab === currTab.tabName) currTabIndex = index
      })

      if (isNumber(currTabIndex) && tabs.length >= currTabIndex) {
        if (tabs[currTabIndex].max_steps > eventTab.step + 1) {
          setEventTab({ tab: eventTab.tab, step: eventTab.step + 1 })
        } else {
          setEventTab({
            tab: tabs[currTabIndex + 1].tabName as IRegistrationEventTabsName,
            step: 0,
          })
        }
      }

      setIsStepLoading(false)
    } catch (error: any) {
      setIsStepLoading(false)
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.message || MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const scrollToTop = () => {
    let main = document.querySelector('.event_comp')

    if (main) main?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }

  const allowAdds =
    event?.EventDetails?.allowAdds ||
    !registration?.isRegistered ||
    profileData.id === event?.EventDetails?.owner

  const isDisabled =
    isStepLoading ||
    registrationLoading ||
    !isLoggedIn ||
    (!grandTotalAccordionData?.amountByPerson?.[0] &&
      eventTab.tab === CONST.UI.REGISTER.TABS.CONFIRMATION) ||
    !allowAdds
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        delay: 0.1,
      }}
      onClick={() => {
        localStorage.setItem('continueForm', JSON.stringify(eventTab))
      }}
      className="lg:absolute z-10 flex-wrap flex gap-3 items-center justify-center lg:bottom-auto lg:top-5 pt-4 px-4 lg:p-0 lg:right-5 text-base"
    >
      {eventTab.tab !== CONST.UI.REGISTER.TABS.TEAM && (
        <button
          onClick={() => {
            let currTabIndex = 0
            tabs.forEach((currTab: any, index: number) => {
              if (eventTab.tab === currTab.tabName) currTabIndex = index
            })

            if (!currTabIndex) return null
            setEventTab({
              tab: tabs[currTabIndex - 1].tabName as IRegistrationEventTabsName,
              step: currTabIndex - 1,
            })
          }}
          className="flex-[1_0_auto] rounded-[10px] border-[1px] transition-all border-SeabiscuitMainThemeColor/5 border-solid text-SeabiscuitMainThemeColor py-2 px-2 lg:min-w-[100px] bg-SeabiscuitMainThemeColor/5 hover:bg-SeabiscuitMainThemeColor hover:text-white"
        >
          <KeyboardArrowLeft fontSize="small" className="!mb-[2px] mr-1 !w-5" />
          Back
        </button>
      )}

      <button
        disabled={isStepLoading}
        className="flex-[1_0_auto] rounded-[10px] border-[1px] transition-all border-SeabiscuitMainThemeColor/5 border-solid text-SeabiscuitMainThemeColor py-2 px-2 lg:min-w-[100px] bg-SeabiscuitMainThemeColor/5  hover:bg-SeabiscuitMainThemeColor hover:text-white"
        type="button"
        onClick={() => history.push(ROUTES_CONST.MY_EVENT.URL)}
      >
        Save & Exit
      </button>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            disabled={isDisabled}
            onClick={() => !isDisabled && handleStep().then()}
            className={clsx(
              'flex-[1_0_auto] whitespace-nowrap rounded-[10px] disabled:opacity-50 disabled:bg-transparent disabled:text-SeabiscuitMainThemeColorDark transition-all border-[1px] border-SeabiscuitMainThemeColor/5 border-solid py-2 px-2 lg:min-w-[100px]',
              eventTab.tab === CONST.UI.REGISTER.TABS.CONFIRMATION
                ? 'bg-SeabiscuitMainThemeColor hover:bg-SeabiscuitMainThemeColorDark text-white'
                : 'bg-SeabiscuitMainThemeColor/5  hover:bg-SeabiscuitMainThemeColor text-SeabiscuitMainThemeColor hover:text-white',
              !isLoggedIn && 'opacity-5'
            )}
          >
            {isStepLoading ? (
              <AutorenewIcon fontSize="small" className="animate-spin" />
            ) : eventTab.tab === CONST.UI.REGISTER.TABS.CONFIRMATION ||
              eventTab.tab === CONST.UI.REGISTER.TABS.CONFIRMATION ? (
              'PAY'
            ) : eventTab.tab === CONST.UI.REGISTER.TABS.TEAM ? (
              'Start registration'
            ) : (
              'Next'
            )}
            {!isStepLoading && eventTab.tab !== CONST.UI.REGISTER.TABS.CONFIRMATION && (
              <KeyboardArrowRight fontSize="small" className="!mb-[2px] ml-1 !w-5" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent hidden={allowAdds}>Disabled by show</TooltipContent>
      </Tooltip>
    </motion.div>
  )
}
